import type { ReactNode } from 'react';
import { Icon } from './Icon.js';
import {
  iconCheck,
  iconCheckCircle,
  iconError,
  iconInfo,
  iconHelp,
  iconWifiOff,
  iconLock,
} from './generated/icons.js';
import { cx } from './util.js';

export interface Stage {
  key: string;
  label: string;
}

/**
 * The deal and form stage tracker. Screen readers hear "Step 2 of 5, Memo, current"; the shape
 * of the current marker is decoration and is hidden from them.
 */
export function StageTracker({
  stages,
  current,
  label,
  announce,
}: {
  stages: Stage[];
  current: number;
  label: string;
  /** The words screen readers hear, for a page in another language. English when omitted. */
  announce?: {
    step: (position: number, total: number) => string;
    state: (state: 'done' | 'current' | 'next') => string;
  };
}) {
  const stepWords =
    announce?.step ?? ((n: number, total: number) => `Step ${String(n)} of ${String(total)}`);
  const stateWords =
    announce?.state ??
    ((state: 'done' | 'current' | 'next') =>
      state === 'done' ? 'done' : state === 'current' ? 'current' : 'next');
  return (
    <ol className="k-stages" aria-label={label}>
      {stages.map((stage, index) => {
        const state = index < current ? 'done' : index === current ? 'current' : 'next';
        return (
          <li
            key={stage.key}
            className={cx('k-stage', `k-stage--${state}`)}
            aria-current={state === 'current' ? 'step' : undefined}
          >
            <span className="k-stage__mark" aria-hidden="true">
              {state === 'done' ? <Icon icon={iconCheck} size={18} /> : index + 1}
            </span>
            <span className="k-stage__label k-t-label-sm">
              <span className="k-sr-only">{`${stepWords(index + 1, stages.length)}, `}</span>
              {stage.label}
              <span className="k-sr-only">{`, ${stateWords(state)}`}</span>
            </span>
          </li>
        );
      })}
    </ol>
  );
}

export interface Signer {
  key: string;
  name: string;
  /** Up to two letters for the circle. */
  initials?: string;
  party: string;
  signed: boolean;
}

/** The three signers. A tick and the word "signed" say who has signed, not colour. */
export function SignerAvatars({
  signers,
  signedLabel,
  waitingLabel,
}: {
  signers: Signer[];
  signedLabel: string;
  waitingLabel: string;
}) {
  return (
    <ul className="k-signers">
      {signers.map((signer) => (
        <li key={signer.key} className={cx('k-signer', signer.signed && 'k-signer--signed')}>
          <span className="k-signer__avatar k-t-label-lg" aria-hidden="true">
            {signer.initials ?? signer.name.slice(0, 2).toUpperCase()}
          </span>
          {signer.signed ? (
            <span className="k-signer__tick" aria-hidden="true">
              <Icon icon={iconCheck} size={14} />
            </span>
          ) : null}
          <span className="k-sr-only">
            {`${signer.party}: ${signer.name}, ${signer.signed ? signedLabel : waitingLabel}`}
          </span>
        </li>
      ))}
    </ul>
  );
}

/** Days left on a renewal. Fifteen or fewer is "hot" and turns to the error colours. */
export function DaysLeftBadge({
  days,
  label,
  unit = 'd',
}: {
  days: number;
  label: string;
  /** The small word under the number: `days` in the design, `d` where room is short. */
  unit?: string;
}) {
  const tone = days <= 15 ? 'hot' : days <= 30 ? 'soon' : 'later';
  return (
    <span className={cx('k-days', `k-days--${tone}`)} role="img" aria-label={label}>
      <span className="k-t-title-sm k-num">{Math.abs(days)}</span>
      <span className="k-t-label-sm" aria-hidden="true">
        {unit}
      </span>
    </span>
  );
}

export type StateKind =
  | 'empty'
  | 'unknown'
  | 'error'
  | 'offline'
  | 'denied'
  | 'locked'
  | 'expired'
  | 'success';

const STATE_ICON = {
  empty: iconInfo,
  unknown: iconHelp,
  error: iconError,
  offline: iconWifiOff,
  denied: iconLock,
  locked: iconLock,
  expired: iconError,
  success: iconCheckCircle,
} as const;

export interface StateScreenProps {
  kind: StateKind;
  /** A smaller state inside a list or a tab: a 64 dp shape and the title in body size. */
  compact?: boolean;
  title: string;
  children?: ReactNode;
  action?: ReactNode;
}

/** Empty, error, offline, denied, locked and expired states. A shape and an icon, plus words. */
export function StateScreen({ kind, compact = false, title, children, action }: StateScreenProps) {
  const alert = kind === 'error' || kind === 'locked';
  return (
    <div
      className={cx('k-state-screen', compact && 'k-state-screen--compact')}
      role={alert ? 'alert' : 'status'}
    >
      <span
        className={cx(
          'k-state-screen__shape',
          (alert || kind === 'expired') && 'k-state-screen__shape--alert',
        )}
        aria-hidden="true"
      >
        <Icon icon={STATE_ICON[kind] ?? iconHelp} size={36} />
      </span>
      <h2 className={compact ? 'k-t-body-lg' : 'k-t-headline'}>{title}</h2>
      {children === undefined ? null : <div className="k-t-body-lg k-muted">{children}</div>}
      {action === undefined ? null : <div className="k-state-screen__action">{action}</div>}
    </div>
  );
}

export interface TimelineEntry {
  key: string;
  title: ReactNode;
  /** Already formatted, with the IST label. */
  time?: string;
  done?: boolean;
  detail?: ReactNode;
}

export function Timeline({ entries, label }: { entries: TimelineEntry[]; label: string }) {
  return (
    <ol className="k-timeline" aria-label={label}>
      {entries.map((entry) => (
        <li
          key={entry.key}
          className={cx('k-timeline__item', entry.done !== false && 'k-timeline__item--done')}
        >
          <span className="k-timeline__dot" aria-hidden="true" />
          <div>
            <p className="k-t-title-sm">{entry.title}</p>
            {entry.detail === undefined ? null : <p className="k-t-body k-muted">{entry.detail}</p>}
            {entry.time === undefined ? null : <p className="k-t-mono k-muted">{entry.time}</p>}
          </div>
        </li>
      ))}
    </ol>
  );
}
